/**
 * CAPA Module dropdown from login session `userData.Modules`.
 * Options are filtered/labelled/disabled per product rules; **User Management**
 * is always injected for CAPA linking. Rows without CAPA flows store ModuleName only.
 */

import { getLoginModuleRows } from "../../../utils/loginModulesUtils";
import { normalizeIsfTemplateCategoryForApi } from "./capaConstants";

/** Prefix for dropdown option values when options come from session rows. */
export const SESSION_MODULE_KEY_PREFIX = "sessmod:";

/** Always shown when building from session — staff cascade / save as user_management. */
export const CAPA_FIXED_USER_MANAGEMENT_OPTION = {
  value: "user_management",
  label: "User Management",
};

/**
 * @param {string | undefined} value
 * @returns {{ value: string; label: string } | null}
 */
export function toCapaModuleOption(value) {
  const v = String(value || "").trim();
  if (!v) return null;
  if (v === "study_management") return { value: v, label: "Study Management" };
  if (v === "user_management") return { value: v, label: "User Management" };
  if (v === "isf") return { value: v, label: "ISF / eISF" };
  return { value: v, label: v.replace(/_/g, " ") };
}

export function normalizeUuid(id) {
  return String(id || "").trim().toLowerCase();
}

export function normalizeModuleName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, " ");
}

/** Session row shown label (Study & User Management → Study Management). */
export function capaDisplayLabelForSessionRow(row) {
  const rawName = normalizeModuleName(row?.ModuleName);
  if (!rawName) {
    return normalizeUuid(row?.ModuleId) || "Module";
  }
  const n = rawName.toLowerCase();
  if (
    (n.includes("study") && n.includes("user")) ||
    /\bstudy\s*&\s*user\b/i.test(rawName) ||
    /\bstudy\s+and\s+user\b/i.test(rawName)
  ) {
    return "Study Management";
  }
  return rawName;
}

export function shouldHideCapaModuleFromDropdown(moduleName) {
  const n = normalizeModuleName(moduleName).toLowerCase();
  if (!n) return true;
  return n === "analytics";
}

/**
 * Modules not ready for CAPA linking — visible but not selectable.
 */
export function isCapaModuleOptionDisabledByName(moduleName) {
  const n = normalizeModuleName(moduleName).toLowerCase();
  if (!n) return false;
  if (n.includes("participant") && n.includes("manager")) return true;
  if (n.includes("e-planner") || /\be\s*planner\b/i.test(moduleName)) return true;
  if (/\bcrf\b/i.test(n)) return true;
  if (n.includes("delegation")) return true;
  if (n.includes("document") && n.includes("management")) return true;
  return false;
}

export function sessionModuleOptionKey(row, index) {
  const id = normalizeUuid(row?.ModuleId);
  if (id) return `${SESSION_MODULE_KEY_PREFIX}${id}`;
  return `${SESSION_MODULE_KEY_PREFIX}i${index}`;
}

export function isSessionModuleOptionValue(value) {
  return String(value || "").startsWith(SESSION_MODULE_KEY_PREFIX);
}

/**
 * @param {string | null | undefined} key
 * @param {Array<{ ModuleId?: string; ModuleName?: string }>} modules
 */
export function findSessionModuleRowByOptionKey(key, modules) {
  if (!Array.isArray(modules) || key == null) return null;
  const s = String(key);
  if (!s.startsWith(SESSION_MODULE_KEY_PREFIX)) return null;
  const rest = s.slice(SESSION_MODULE_KEY_PREFIX.length);
  if (/^i\d+$/.test(rest)) {
    const idx = Number(rest.slice(1));
    return idx >= 0 && idx < modules.length ? modules[idx] : null;
  }
  const id = rest.toLowerCase();
  return modules.find((m) => normalizeUuid(m.ModuleId) === id) || null;
}

/**
 * CAPA slug(s) inferred from **ModuleName** (linking UX / save).
 * Study & User Management maps to **study only** (User Management is a separate fixed option).
 * @param {{ ModuleId?: string; ModuleName?: string }} entry
 * @returns {string[]}
 */
export function capaSlugsForSessionModuleEntry(entry) {
  if (!entry || typeof entry !== "object") return [];
  const rawName = normalizeModuleName(entry.ModuleName);
  if (!rawName) return [];
  const n = rawName.toLowerCase();

  if (
    (n.includes("study") && n.includes("user")) ||
    /\bstudy\s*&\s*user\b/i.test(rawName) ||
    /\bstudy\s+and\s+user\b/i.test(rawName)
  ) {
    return ["study_management"];
  }
  if (n.includes("participant") && n.includes("manager")) {
    return ["study_management"];
  }
  const trimmed = rawName.trim();
  if (
    /^eisf$/i.test(trimmed) ||
    /\beisf\b/i.test(rawName) ||
    (/^(e\-?\s*)?isf$/i.test(trimmed) && !n.includes("planner"))
  ) {
    return ["isf"];
  }
  return [];
}

/**
 * Session-derived options (filtered, renamed, disabled flags). Does **not** include All or fixed User Management.
 */
export function capaModuleDropdownOptionsFromSessionModules(modules) {
  if (!Array.isArray(modules)) return [];
  const out = [];
  for (let i = 0; i < modules.length; i++) {
    const row = modules[i];
    const rawName = normalizeModuleName(row?.ModuleName);
    if (!rawName || shouldHideCapaModuleFromDropdown(rawName)) continue;
    if (/^user\s*management$/i.test(rawName)) continue;

    out.push({
      value: sessionModuleOptionKey(row, i),
      label: capaDisplayLabelForSessionRow(row),
      disabled: isCapaModuleOptionDisabledByName(rawName),
    });
  }
  return out;
}

export function shouldUseSessionCapaModules(userData) {
  return getLoginModuleRows(userData) !== null;
}

export function resolveCapaModuleDropdownOptions(userData, fallbackSlugs = []) {
  const sessionRows = getLoginModuleRows(userData);
  if (sessionRows !== null) {
    const sessionOpts = capaModuleDropdownOptionsFromSessionModules(sessionRows);
    return [CAPA_FIXED_USER_MANAGEMENT_OPTION, ...sessionOpts];
  }
  const slugs = Array.isArray(fallbackSlugs) ? fallbackSlugs : [];
  return slugs.map((s) => toCapaModuleOption(String(s || "").trim())).filter(Boolean);
}

/** UI sentinel — saved CAPA uses null/empty Module + no entity context. */
export const CAPA_MODULE_ALL_VALUE = "__capa_all__";

export const CAPA_MODULE_OPTION_ALL = {
  value: CAPA_MODULE_ALL_VALUE,
  label: "All",
};

export function isCapaModuleAllValue(moduleSlug) {
  const v = moduleSlug == null ? "" : String(moduleSlug).trim();
  return (
    v === "" ||
    v === CAPA_MODULE_ALL_VALUE ||
    v.toLowerCase() === "all"
  );
}

export function capaModuleDropdownOptionsWithAll(options) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const rest = list.filter((o) => !isCapaModuleAllValue(o?.value));
  return [CAPA_MODULE_OPTION_ALL, ...rest];
}

/** @param {string[]} slugs from {@link capaSlugsForSessionModuleEntry} or legacy slug */
export function getCapaEntityUiMode(slugs) {
  if (!Array.isArray(slugs) || slugs.length === 0) return "none";
  if (slugs.includes("isf")) return "isf";
  if (slugs.includes("study_management")) return "study";
  if (slugs.includes("user_management")) return "staff";
  return "none";
}

/**
 * Resolve CAPA link slugs from dropdown value (session key or legacy slug).
 */
export function resolveCapaLinkSlugsForDropdownValue(selectedValue, userData) {
  if (isCapaModuleAllValue(selectedValue)) return [];
  const v = String(selectedValue || "").trim();
  if (v === "user_management") return ["user_management"];
  if (
    userData &&
    shouldUseSessionCapaModules(userData) &&
    isSessionModuleOptionValue(selectedValue)
  ) {
    const row = findSessionModuleRowByOptionKey(selectedValue, userData.Modules);
    return row ? capaSlugsForSessionModuleEntry(row) : [];
  }
  if (v === "study_management" || v === "isf") return [v];
  return [];
}

export function validateCapaCreateModuleSelection({
  selectedValue,
  userData,
  selectedStudy,
  selectedStaff,
  selectedDocument,
  isfStudy,
  isfCategory,
}) {
  if (isCapaModuleAllValue(selectedValue)) return { ok: true };
  const slugs = resolveCapaLinkSlugsForDropdownValue(selectedValue, userData);
  const mode = getCapaEntityUiMode(slugs);
  if (mode === "none") return { ok: true };
  if (mode === "study") {
    return selectedStudy?.value
      ? { ok: true }
      : { ok: false, message: "Please select a study." };
  }
  if (mode === "staff") {
    return selectedStaff?.value
      ? { ok: true }
      : { ok: false, message: "Please select a staff member." };
  }
  if (mode === "isf") {
    if (!isfStudy?.value || !isfCategory?.value) {
      return {
        ok: false,
        message: "Please select study and ISF category.",
      };
    }
    if (!selectedDocument?.value) {
      return { ok: false, message: "Please select a document." };
    }
    return { ok: true };
  }
  return { ok: true };
}

export function buildCapaCreateModulePayload({
  selectedValue,
  userData,
  selectedStudy,
  selectedStaff,
  selectedDocument,
  isfStudy,
  isfCategory,
}) {
  if (isCapaModuleAllValue(selectedValue)) {
    return {
      module: null,
      entityType: null,
      entityId: null,
      entityLabel: null,
      studyIdForIsf: undefined,
      templateCategoryForIsf: undefined,
    };
  }
  const slugs = resolveCapaLinkSlugsForDropdownValue(selectedValue, userData);
  const mode = getCapaEntityUiMode(slugs);

  if (mode === "none") {
    const row =
      userData &&
      shouldUseSessionCapaModules(userData) &&
      isSessionModuleOptionValue(selectedValue)
        ? findSessionModuleRowByOptionKey(selectedValue, userData.Modules)
        : null;
    const name = row ? normalizeModuleName(row.ModuleName) : "";
    const mod = name ? name.slice(0, 100) : null;
    return {
      module: mod,
      entityType: null,
      entityId: null,
      entityLabel: null,
      studyIdForIsf: undefined,
      templateCategoryForIsf: undefined,
    };
  }
  if (mode === "study") {
    return {
      module: "study_management",
      entityType: "study",
      entityId: selectedStudy?.value ?? null,
      entityLabel: selectedStudy?.label ?? null,
      studyIdForIsf: undefined,
      templateCategoryForIsf: undefined,
    };
  }
  if (mode === "staff") {
    return {
      module: "user_management",
      entityType: "staff",
      entityId: selectedStaff?.value ?? null,
      entityLabel: selectedStaff?.label ?? null,
      studyIdForIsf: undefined,
      templateCategoryForIsf: undefined,
    };
  }
  if (mode === "isf") {
    return {
      module: "isf",
      entityType: "document",
      entityId: selectedDocument?.value ?? null,
      entityLabel: selectedDocument?.label ?? null,
      studyIdForIsf: isfStudy?.value,
      templateCategoryForIsf: normalizeIsfTemplateCategoryForApi(isfCategory?.value),
    };
  }
  return {
    module: null,
    entityType: null,
    entityId: null,
    entityLabel: null,
    studyIdForIsf: undefined,
    templateCategoryForIsf: undefined,
  };
}

/**
 * Match stored CAPA.Module (slug or custom name) to a session row for display.
 */
export function sessionModuleOptionForStoredCapa(detail, modules) {
  if (!detail || !Array.isArray(modules)) return null;
  const dm = String(detail.Module || "").trim();
  if (!dm) return null;
  if (isCapaModuleAllValue(dm)) return CAPA_MODULE_OPTION_ALL;
  if (dm === "user_management") return CAPA_FIXED_USER_MANAGEMENT_OPTION;

  for (let i = 0; i < modules.length; i++) {
    const row = modules[i];
    const name = normalizeModuleName(row.ModuleName);
    if (name && name === dm) {
      return {
        value: sessionModuleOptionKey(row, i),
        label: capaDisplayLabelForSessionRow(row),
      };
    }
    if (normalizeUuid(row.ModuleId) && normalizeUuid(row.ModuleId) === normalizeUuid(dm)) {
      return {
        value: sessionModuleOptionKey(row, i),
        label: capaDisplayLabelForSessionRow(row),
      };
    }
    const slugs = capaSlugsForSessionModuleEntry(row);
    if (slugs.includes(dm)) {
      return {
        value: sessionModuleOptionKey(row, i),
        label: capaDisplayLabelForSessionRow(row),
      };
    }
  }
  return null;
}
